import Vue from 'vue'
import { uistate } from '../helpers/consts'
import { generateTimestamp } from '../helpers/helpers'
import { trigger, triggerType } from '@/helpers/Tracker'
import {
  EVENT,
  listenToCustomEvent,
  dispatchCustomEvent
} from '@/helpers/customEvents'

/* uistate properties:

windowOpen - boolean: is chat popup open?
connected - boolean:

nudge - smaller popup, not in "main" container

ui main states (if windowOpen=true):
INIT connecting - initital state - check chat availability (and establish connection?)
NO_CONNECTION
SESSION_LOST
OUTSIDE_SERVICE_HOURS
NO_AGENT_AVAILABLE
READY - chat is available but not established yet
CONVERSATION - conversation "in progress"
CONVERSATION_SUSPENDED - prompt to send transcript with possibility to get back to conversation
CONVERSATION_ENDED - prompt to send transcript
CONVERSATION_RATING - show rating / feedback
CONVERSATION_RATED - end-state; nothing more to do for the current conversation
CONVERSATION_NEW - for customer flow; conversation is ended and user can chose to start new conversation
CONVERSATION_TRANSCRIPT - send transcript to Sanitas
*/

const defaultAgentAvatar = require('@/assets/images/contact.png')
const defaultIncomingIcon = require('@/assets/images/sanitas.svg')
const chatbotIncomingIcon = require('@/assets/images/chatbot.png')
const aiaiBotIncomingIcon = require('@/assets/images/alva.svg')
const sliderSwipeInstruction = require('@/assets/images/slider-swipe-instruction.png')
const defaultAgentScreenName = 'Sanitas'
const { agentTypingTimeout } = uistate

const state = {
  lang: 'de',
  displayMode: 'normal',
  customerFlow: false,
  isArchiveView: false,
  windowOpen: false,
  windowScrolledUp: false,
  conversationState: 'INIT',
  agentAvatar: defaultAgentAvatar,
  agentScreenName: defaultAgentScreenName,
  transcriptEmail: '',
  transcriptState: 'dataTransfer',
  agentTypingIndicator: {
    agentTyping: '',
    timestamp: 0,
    agentTypingTimeout
  },
  chatFab: {
    hideFab: false
  },
  restoringSession: false,
  timeUntilClosing: null,
  sanitasSystemNotifications: [],
  outsideServiceHoursMessage: null,
  outsideServiceHoursDueToHoliday: false,
  apiHandlersRegistered: false,
  fullScreen: false,
  aiaiBotDisclaimerAccepted: null,
  loadingLabels: false,
  showTNPSModal: false
}

const getters = {
  getBehavior: () => sessionStorage.behavior || 'active',
  chatFabHidden: state => state.chatFab.hideFab,
  webView: state => state.displayMode === 'webview',
  isCustomerFlow: state => state.customerFlow,
  isAiaiBotEnabled: (state, getters, rootState) => (
    process.env.VUE_APP_AIAIBOT_ACTIVE &&
    state.customerFlow &&
    !rootState.conversation.isNonCustomer
  ),
  isAiaiBotActive: (state, getters, rootState) => {
    if (!getters.isAiaiBotEnabled) {
      return false
    }

    const convStore = rootState.conversation
    const conv = convStore.conversation

    if (conv?.conversationType === 'AIAIBOT') {
      return conv?.status === 'ACTIVE'
    }

    if (!conv || conv?.status === 'INIT') {
      return state.lang === 'de' && state.aiaiBotDisclaimerAccepted !== false
    }

    // Handover to live chat pending
    if (
      conv?.conversationType === 'CHAT' &&
      Boolean(convStore.chatBotStoryId) &&
      conv?.status === 'ACTIVE' &&
      conv?.participants.length === 1
    ) {
      return true
    }

    console.warn('isAiaiBotActive: unknown use case...')

    return false
  },
  isArchiveView: state => state.isArchiveView,
  isArchiveWebview: state => state.archiveWebview,
  defaultAgentAvatar () {
    return defaultAgentAvatar
  },
  defaultAgentScreenName () {
    return defaultAgentScreenName
  },
  defaultIncomingIcon () {
    return defaultIncomingIcon
  },
  chatbotIncomingIcon () {
    return chatbotIncomingIcon
  },
  aiaiBotIncomingIcon () {
    return aiaiBotIncomingIcon
  },
  sliderSwipeInstruction () {
    return sliderSwipeInstruction
  },
  chatIsOpen: state => state.windowOpen,
  chatAvailable: state => state.conversationState === 'READY',
  agentIsTyping: state => state.agentTypingIndicator.agentTyping === 'AGENT_TYPING',
  agentIsNotTyping: state => state.agentTypingIndicator.agentTyping === 'AGENT_NOT_TYPING',
  agentTypingTimeout: state => state.agentTypingIndicator.agentTypingTimeout,
  agentIsTypingTimestamp: state => state.agentTypingIndicator.timestamp,
  getTimeUntilClosing: state => state.timeUntilClosing,
  getSanitasSystemNotifications: state => state.sanitasSystemNotifications || [],
  getFullScreen: state => state.fullScreen
}

const actions = {
  init ({ commit, state, dispatch, getters }, { config }) {
    // console.log('uistate INIT', config, session)
    // events handler
    if (!state.apiHandlersRegistered) {
      this._vm.$chatApi.registerEventHandler((event) => {
        switch (event.type) {
          case 'AGENT_JOINED_CONVERSATION':
          case 'AGENT_IS_IN_CONVERSATION':
            dispatch('updateAgentAvatar', Vue.prototype.$chatApi.settings.baseUrl + '/profile/' + event.payload.senderId + '/profilePicture' || event.payload.pictureUrl)
            dispatch('updateAgentScreenName', event.payload.screenName)
            break
          case 'AGENT_LEFT_CONVERSATION':
            dispatch('resetAgentAvatar')
            dispatch('resetAgentScreenName')
            dispatch('updateAgentTyping')
            break
          case 'CONVERSATION_SUSPENDED':
            dispatch('resetAgentAvatar')
            dispatch('resetAgentScreenName')
            dispatch('updateAgentTyping')
            commit('SET_CONVERSATION_STATE', 'CONVERSATION_SUSPENDED')
            break
          case 'CONVERSATION_ENDED':
            dispatch('resetAgentAvatar')
            dispatch('resetAgentScreenName')
            dispatch('updateAgentTyping')
            if (!getters.isCustomerFlow) {
              commit('SET_CONVERSATION_STATE', 'CONVERSATION_ENDED')
            }
            break
          case 'CONVERSATION_ENDED_BY_AGENT':
            if (!getters.isCustomerFlow) {
              commit('SET_TRANSCRIPT_STATE', 'dataTransfer')
              commit('SET_CONVERSATION_STATE', 'CONVERSATION_TRANSCRIPT')
            } else {
              dispatch('conversation/messageReceived', {
                msgType: 'FINISH',
                screenName: state.agentScreenName,
                msgId: event.payload.msgId
              }, {root: true})
              dispatch('conversation/removeAgentFromConversation', {}, { root: true })
              dispatch('continueForCustomer')
            }
            break
          case 'AGENT_TYPING':
            dispatch('updateAgentTyping', { agentTyping: event.payload, timestamp: generateTimestamp() })
            break
          case 'AGENT_NOT_TYPING':
            dispatch('updateAgentTyping', { agentTyping: event.payload, timestamp: 0 })
            break
          case 'BACKEND_DOWN':
            commit('SET_CONVERSATION_STATE', 'NO_CONNECTION')
            break
          case 'SESSION_LOST':
            commit('SET_CONVERSATION_STATE', 'SESSION_LOST')
            dispatch('conversation/reset', null, { root: true })
            break
          case 'CHATBOT_ACTIVE':
            dispatch('conversation/chatbotActive', event.payload, { root: true })
            break
          case 'SET_HIDE_FAB':
            commit('SET_HIDE_FAB', event.payload)
            break
          case 'CONVERSATION_NEW':
            dispatch('conversation/conversationClose')
            break
          default:
            // console.log('Unknown event type: ' + event.type)
        }
      })
      commit('SET_API_HANDLERS_REGISTERED', true)
    }

    if (config.lang) {
      commit('SET_LANG', config.lang)
    }

    dispatch('getSanitasNotifications', config.lang)

    const open = () => {
      dispatch('open').then(() => {
        dispatchCustomEvent(EVENT.SEND.CONVERSATION_STARTED)
      })
    }

    setTimeout(() => {
      if (getters.webView) {
        open()
      }

      const shouldOpenFabOnPageLoad =
        getters.chatFabHidden &&
        !getters.isCustomerFlow &&
        this._vm.$session.get('WEBSITEuiConvState') === 'READY' &&
        Boolean(this._vm.$session.get('WEBSITEchatOpen')) &&
        Boolean(this._vm.$session.get('WEBSITEconvId'))

      if (shouldOpenFabOnPageLoad) {
        // Show Fab
        commit('SET_HIDE_FAB', false)

        // Triggering this event hides the .com Fab widget as described here:
        // https://jira-produktion.intra01.sanitas.com/browse/SPPO-7912
        dispatchCustomEvent(EVENT.SEND.CONVERSATION_STARTED)
      }

    }, 0)

    listenToCustomEvent(EVENT.LISTEN_TO.OPEN, (e) => {
      if (e && e.detail) {
        sessionStorage.behavior = e.detail.behavior
      }

      trigger(triggerType.ACTION, 'ChatLinkClicked')
      open()
    })
  },

  getSanitasNotifications ({ commit }, lang) {
    this._vm.$chatApi.getSanitasSystemNotifications(lang).then(response => {
      commit('SET_SANITAS_SYSTEM_NOTIFICATIONS', response.data.notifications)
    }).catch(() => {
      commit('SET_SANITAS_SYSTEM_NOTIFICATIONS', [])
    })
  },

  // set conversationState for, for example, nudging component
  // @TODO: how to check if agent availability changed?
  // @TODO: how to handle conversationState 'INIT and 'READY'?
  checkAgentAvailable ({ commit, state, rootState, dispatch, rootGetters, getters }) {
    if (rootState.conversation &&
        rootState.conversation.conversation &&
        rootState.conversation.conversation.messages?.find(m => m.msgType === 'JOIN')) {
      return {}
    } else {
      return this._vm.$chatApi.getChatAvailable(state.lang)
        .then(result => {
          commit('SET_TIME_UNTIL_CLOSING', {
            timeUntilClosing: result.timeUntilClosing
          })
        
        dispatch('conversation/setClosingTime', {
          closingTime: generateTimestamp() + result.timeUntilClosing
        }, {root: true})
        
        if (!result.systemReachable) {
          commit('SET_CONVERSATION_STATE', 'NO_CONNECTION')
        } else if (!result.isOpeningHours) {
          const {conversation} = rootState.conversation
          
          if (!conversation || conversation.convId < 0) {
            commit('SET_CONVERSATION_STATE', 'OUTSIDE_SERVICE_HOURS')
            commit('SET_OUTSIDE_SERVICE_HOURS_MESSAGE', {
              message: result.outsideServiceHoursMessage,
              isHoliday: result.reason === 'public_holiday'
            })
          }
        } else if (
          !result.isAgentAvailable &&
          !rootGetters['conversation/isActiveConversation']
        ) {
          commit('SET_CONVERSATION_STATE', 'NO_AGENT_AVAILABLE')
          
          // reset acceptedTerms when no message has been sent by enduser
          
          if (rootState.conversation.conversation && rootState.conversation.conversation.convId < 0) {
            dispatch('conversation/setAcceptedTerms', false, {root: true})
          }
        } else if (!getters.isCustomerFlow) {
          commit('SET_CONVERSATION_STATE', 'READY')
        }

        return result
      })
    }
  },

  setChatFab ({ commit }, payload) {
    commit('SET_CHAT_FAB', payload)
  },

  setArchiveView ({ commit }, payload) {
    commit('SET_ARCHIVE_VIEW', payload)
  },

  setCustomerFlow ({ commit }, payload) {
    commit('SET_CUSTOMER_FLOW', payload)
  },

  open ({ commit, state, dispatch, getters, rootState, rootGetters }) {
    commit('SET_OPEN', true)
    dispatchCustomEvent(EVENT.SEND.OPENED)
    document.getElementsByTagName('body')[0].classList.add('chat-open')

    // if there is a back-to-top element, hide it.
    let backtotop = document.getElementsByTagName('sanitas-scroll-to-top')
    if (backtotop.length > 0) {
      backtotop[0].style.display = 'none'
    }
    if (state.conversationState === 'READY') {
      dispatch('checkAgentAvailable')
    }

    if (
      state.conversationState === 'INIT' ||
      state.conversationState === 'NO_CONNECTION' ||
      state.conversationState === 'OUTSIDE_SERVICE_HOURS' ||
      state.conversationState === 'NO_AGENT_AVAILABLE'
    ) {
      let deferred
      if (state.restoringSession || (rootState.conversation.conversation && rootState.conversation.conversation.convId > 1)) {
        deferred = Promise.resolve(undefined)
        return deferred.then(() => {
          commit('SET_CONVERSATION_STATE', 'READY')
          return true
        })
      } else {
        deferred = this._vm.$chatApi.getExistingConversation()
      }
      return deferred
        .then(result => {
          if (result || getters.isCustomerFlow) {
            // console.log('uistate/open getExistingConversation: ', result)
            dispatch('conversation/init', { session: result }, { root: true })
              .then(() => {
                if (
                  rootState.conversation.conversation &&
                  rootState.conversation.conversation.status === 'ENDUSER_SUSPENDED'
                ) {
                  commit('SET_CONVERSATION_STATE', 'CONVERSATION_SUSPENDED')
                } else if (
                  rootState.conversation.conversation &&
                  rootState.conversation.conversation.status === 'ENDUSER_ENDED') {
                  commit('SET_CONVERSATION_STATE', 'CONVERSATION_RATING')
                } else {
                  commit('SET_CONVERSATION_STATE', 'READY')
                }
              })
          } else {
            // no existing conversation -> check availability
            return this._vm.$chatApi.getChatAvailable(state.lang)
              .then(result => {
                commit('SET_TIME_UNTIL_CLOSING', {
                  timeUntilClosing: result.timeUntilClosing
                })
                
                dispatch('conversation/setClosingTime', {
                  closingTime: generateTimestamp() + result.timeUntilClosing
                }, {root: true})
                
                // console.log('open / INIT / getChatAvailable: ', result)
              
                if (!result.systemReachable) {
                  commit('SET_CONVERSATION_STATE', 'NO_CONNECTION')
                } else if (!result.isOpeningHours) {
                  commit('SET_CONVERSATION_STATE', 'OUTSIDE_SERVICE_HOURS')
                  commit('SET_OUTSIDE_SERVICE_HOURS_MESSAGE', {
                    message: result.outsideServiceHoursMessage,
                    isHoliday: result.reason === 'public_holiday'
                  })
                } else if (
                  !result.isAgentAvailable &&
                  !rootGetters['conversation/isActiveConversation']
                ) {
                  commit('SET_CONVERSATION_STATE', 'NO_AGENT_AVAILABLE')
                } else {
                  // check if there is a pre-existing active chat, if yes connect etc.
                  // otherwise goto state READY
                  commit('SET_CONVERSATION_STATE', 'READY')
                }
                
                return true
              })
          }
        })
    }

    return Promise.resolve(true)
  },

  close ({ commit, state, getters }, eventType) {
    // reactivate FAB to be sure that if the chat window is minimized, the user will
    // see the "new messages" notifications.
    commit('SET_HIDE_FAB', getters.getBehavior === 'passive' && eventType !== 'minimize')
    commit('SET_OPEN', false)
    document.getElementsByTagName('body')[0].classList.remove('chat-open')
    // if there is a back-to-top element, reshow it.
    let backtotop = document.getElementsByTagName('sanitas-scroll-to-top')
    if (backtotop.length > 0) {
      backtotop[0].style.cssText = ''
    }
    if (state.conversationState !== 'SESSION_LOST') {
      commit('SET_CONVERSATION_STATE', 'INIT')
    }
    commit('SET_AGENT_AVATAR', defaultAgentAvatar)
    commit('SET_AGENT_SCREENNAME', defaultAgentScreenName)
    return true
  },

  minimizeFab ({ commit }) {
    // reactivate FAB to be sure that if the chat window is minimized, the user will
    // see the "new messages" notifications.
    commit('SET_HIDE_FAB', false)
    commit('SET_OPEN', false)
    document.getElementsByTagName('body')[0].classList.remove('chat-open')
  },
  continueForCustomer ({ dispatch, commit, rootState }) {
    const conversationType = rootState
      .conversation?.conversation?.conversationType

    dispatch('resetAgentAvatar')
    dispatch('resetAgentScreenName')
    dispatch('updateAgentTyping')
    commit(
      'conversation/SET_CONVERSATION_STATUS',
      conversationType === 'AIAIBOT' ? 'AIAIBOT_ENDED' : 'AGENT_ENDED',
      { root: true }
    )
    commit('SET_CONVERSATION_STATE', 'READY')
    dispatch('addMessagesForEndConversation')
  },

  updateAgentAvatar ({ commit }, payload) {
    commit('SET_AGENT_AVATAR', payload)
  },

  resetAgentAvatar ({ commit }) {
    commit('SET_AGENT_AVATAR', defaultAgentAvatar)
  },

  updateAgentScreenName ({ commit }, payload) {
    commit('SET_AGENT_SCREENNAME', payload)
  },

  resetAgentScreenName ({ commit }) {
    commit('SET_AGENT_SCREENNAME', defaultAgentScreenName)
  },

  updateAgentTyping ({ commit }, payload) {
    commit('SET_AGENT_TYPING', payload)
  },

  setRestoringSession ({ commit }, payload) {
    commit('SET_RESTORING_SESSION', payload)
  },

  setTimeClosingNotifications ({state, dispatch, rootState}) {
    if (state.timeUntilClosing) {
      let untilClosingTimeNotification = state.timeUntilClosing * 1000
      let untilFirstNotification = (state.timeUntilClosing - (15 * 60)) * 1000
      const conversationState = rootState.conversation
      setTimeout(() => {
        let convId = conversationState.conversation.convId
        if (convId === -1) {
          dispatch('conversation/showFirstNotification', { session: {} }, { root: true })
        }
      }, untilFirstNotification)
      setTimeout(() => {
        let convId = conversationState.conversation.convId
        if (convId === -1) {
          dispatch('conversation/showCloseTimeNotification', { session: {} }, { root: true })
        }
      }, untilClosingTimeNotification)
    }
  },

  addMessagesForEndConversation ({ dispatch }) {
    let messageTranscriptYesNo = {
      convId: -1,
      msgLabelKey: 'offer_conv_transcript',
      senderId: -2000,
      screenName: 'Sanitas',
      msgType: 'YES_NO'
    }
    return dispatch('conversation/addMessage', messageTranscriptYesNo, { root: true })
  },

  setConversationState ({ commit }, payload) {
    commit('SET_CONVERSATION_STATE', payload)
  },

  updateUILanguage ({ commit, dispatch, getters, rootState }, payload) {
    // Prevent language switch if aiaibot conversation is active
    if (getters.isAiaiBotActive &&
      rootState.conversation?.conversation?.status === 'ACTIVE'
    ) {
      return true
    }

    commit('SET_LANG', payload.lang)
    dispatch('conversation/reloadMessages', {}, {root: true})
  },

  toggleFullScreen ({commit, state}) {
    commit('TOOGLE_FULL_SCREEN', !state.fullScreen)
  },

  acceptAiaiBotDisclaimer ({ commit }) {
    commit('SET_AIAIBOT_DISCLAIMER_ACCEPTED', true)
  },

  refuseAiaiBotDisclaimer ({ commit }) {
    commit('SET_AIAIBOT_DISCLAIMER_ACCEPTED', false)
  },

  clearAiaiBotDisclaimer ({ commit }) {
    commit('SET_AIAIBOT_DISCLAIMER_ACCEPTED', null)
  },

  setShowTNPSModal ({ commit }, value) {
    commit('SET_SHOW_TNPS_MODAL', value)
  }
}

const mutations = {
  SET_LANG (state, lang) {
    state.lang = lang
  },
  SET_OPEN (state, isOpen) {
    state.windowOpen = isOpen
  },
  SET_CONVERSATION_STATE (state, conversationState) {
    state.conversationState = conversationState
  },

  SET_AGENT_AVATAR (state, value) {
    state.agentAvatar = value
  },

  SET_AGENT_SCREENNAME (state, value) {
    state.agentScreenName = value
  },

  SET_TRANSCRIPT_EMAIL (state, value) {
    state.transcriptEmail = value
  },

  SET_TRANSCRIPT_STATE (state, value) {
    state.transcriptState = value
  },

  SET_AGENT_TYPING (state, payload) {
    state.agentTypingIndicator = { ...state.agentTypingIndicator, ...payload }
  },

  SET_CHAT_FAB (state, payload) {
    state.chatFab = { ...state.chatFab, hideFab: payload.hideFab }
    if (payload && payload.displayMode) {
      state.displayMode = payload.displayMode
    }
  },

  SET_ARCHIVE_VIEW (state, payload) {
    state.isArchiveView = payload
  },

  SET_CUSTOMER_FLOW (state, payload) {
    state.customerFlow = payload.isCustomerFlow
  },

  SET_HIDE_FAB (state, value) {
    Vue.set(state.chatFab, 'hideFab', value)
  },

  SET_WINDOW_SCROLLED_UP (state, payload) {
    state.windowScrolledUp = payload
  },

  SET_RESTORING_SESSION (state, value) {
    state.restoringSession = value
  },

  SET_TIME_UNTIL_CLOSING (state, payload) {
    state.timeUntilClosing = payload.timeUntilClosing
  },

  SET_SANITAS_SYSTEM_NOTIFICATIONS (state, payload) {
    state.sanitasSystemNotifications = payload
  },

  SET_OUTSIDE_SERVICE_HOURS_MESSAGE (state, payload) {
    state.outsideServiceHoursMessage = payload.message
    state.outsideServiceHoursDueToHoliday = payload.isHoliday
  },

  SET_API_HANDLERS_REGISTERED (state, payload) {
    state.apiHandlersRegistered = payload
  },

  TOOGLE_FULL_SCREEN (state, payload) {
    state.fullScreen = payload
  },

  SET_AIAIBOT_DISCLAIMER_ACCEPTED (state, payload) {
    state.aiaiBotDisclaimerAccepted = payload
  },

  SET_LOADING_LABELS (state, value) {
    state.loadingLabels = value
  },

  SET_SHOW_TNPS_MODAL (state, value) {
    state.showTNPSModal = value
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
